export const PLATFORM_GRAMMAR = {
  instagram: {
    charset: /^[a-z0-9._]+$/,
    min: 3,
    max: 30,
    hyphen: false,
    // Instagram route segments, never usernames.
    reserved: new Set(['p', 'reel', 'reels', 'tv', 'stories', 'story', 'explore', 's',
      'accounts', 'about', 'directory', 'web', 'developer', 'legal', 'privacy', 'reels_audio']),
  },
  tiktok: { charset: /^[a-z0-9._]+$/, min: 2, max: 24, hyphen: false, reserved: new Set() },
  youtube: {
    charset: /^[a-z0-9._-]+$/, min: 3, max: 30, hyphen: true,
    reserved: new Set(['channel', 'c', 'user', 'watch', 'shorts', 'playlist']),
  },
  facebook: {
    charset: /^[a-z0-9.]+$/, min: 1, max: 60, hyphen: false,
    reserved: new Set(['groups', 'pages', 'events', 'profile.php']),
  },
  threads: {
    charset: /^[a-z0-9._]+$/,
    min: 3,
    max: 30,
    hyphen: false,
    reserved: new Set(['search', 'login', 'settings', 'activity', 'intent', 't']),
  },
  x: {
    charset: /^[a-z0-9_]+$/,
    min: 1,
    max: 15,
    hyphen: false,
    reserved: new Set(['home', 'explore', 'search', 'i', 'intent', 'settings', 'notifications',
      'messages', 'hashtag', 'share', 'login', 'signup', 'compose', 'tos', 'privacy']),
  },
};

/** The manager-facing text for each refusal code — one wording, both sides. */
export const HANDLE_ERROR_RU = {
  hyphen: 'В нике есть дефис — у {hint} таких ников не бывает. Проверьте ссылку и вставьте её целиком.',
  'wrong-platform': 'Это ссылка на {detected}, а поле — {hint}. Вставьте ссылку в нужное поле.',
  junk: 'Это адрес сайта или служебное слово, а не ник. Вставьте ссылку на профиль или сам ник.',
  unparseable: 'Не удалось разобрать ник. Вставьте ссылку на профиль целиком.',
};

const JUNK_TOKENS = new Set([
  'instagram.com', 'www.instagram.com', 'tiktok.com', 'www.tiktok.com',
  'youtube.com', 'www.youtube.com', 'facebook.com', 'www.facebook.com',
  'gmail.com', 'www', 'com', 'http', 'https', 'https:',
]);
const JUNK_SUFFIX = /\.(com|net|org|ru)$/;
const NON_ASCII = /[^\x20-\x7E]/;

const err = (error, detected, candidate) => {
  const out = { error };
  if (detected) out.detected = detected;
  if (candidate !== undefined && candidate !== null && candidate !== '') out.candidate = candidate;
  return out;
};

function clean (input) {
  if (typeof input !== 'string') return '';
  let s = input.replace(/[\n\r\t]/g, '').replace(/\\/g, '');
  const md = s.match(/\[([^\]]*)\]\(([^)]*)\)/);
  if (md) s = `${md[1]} ${md[2]}`;
  s = s.trim().replace(/^[<[(*]+/, '').replace(/[>\])*]+$/, '');
  return s.trim();
}

function extract (s) {
  const fb = s.match(/facebook\.com\/profile\.php\?[^\s]*id=(\d+)/i);
  if (fb) return { candidate: fb[1], detected: 'facebook', source: 'url' };

  const fbShare = s.match(/facebook\.com\/share\/([^/?#\s]+)(\/?)/i);
  if (fbShare) {
    return {
      candidate: fbShare[1], detected: 'facebook', source: 'url',
      contentJunk: /^[rvp]$/i.test(fbShare[1]) && fbShare[2] === '/',
    };
  }
  const fbPeople = s.match(/facebook\.com\/people\/[^/?#\s]+\/(\d+)/i);
  if (fbPeople) return { candidate: fbPeople[1], detected: 'facebook', source: 'url' };

  const ytPath = s.match(/youtube\.com\/(c|user|channel)\/([^/?#\s]+)/i);
  if (ytPath) {
    return {
      candidate: ytPath[2], detected: 'youtube', source: 'url',
      contentJunk: ytPath[1].toLowerCase() === 'channel',
    };
  }

  const url = s.match(/(?:(instagram|tiktok|youtube|facebook)\.com|(youtu)\.be)\/([^/?#\s[\]()<>]+)/i);
  if (url) {
    return {
      candidate: url[3],
      detected: url[2] ? 'youtube' : url[1].toLowerCase(),
      source: 'url',
      contentJunk: Boolean(url[2]),
    };
  }

  const mention = s.match(/(?:^|[\s([])@([^\s@[\]()<>]+)/);
  if (mention) return { candidate: mention[1], source: 'mention' };

  const threads = s.match(/(?:^|[\s/.])threads\.(?:net|com)\/@?([^/?#\s[\]()<>]+)/i);
  if (threads) return { candidate: threads[1], detected: 'threads', source: 'url' };

  const x = s.match(/(?:^|[\s/.])(?:x|twitter)\.com\/@?([^/?#\s[\]()<>]+)/i);
  if (x) return { candidate: x[1], detected: 'x', source: 'url' };

  return { candidate: s, source: 'bare' };
}

/**
 * Parse a raw value into a canonical handle, or a typed refusal.
 */
export function parse (input, platformHint) {
  const s = clean(input);
  if (!s) return err('unparseable');

  const { candidate: raw, detected, source, contentJunk } = extract(s);
  const candidate = String(raw).replace(/^@+/, '').replace(/\/+$/, '');
  if (!candidate) return err('unparseable', detected);

  const hint = platformHint || null;
  if (detected && hint && detected !== hint) return err('wrong-platform', detected, candidate);
  const platform = hint || detected;
  const grammar = platform ? PLATFORM_GRAMMAR[platform] : null;
  if (!grammar) return err('unparseable', detected, candidate);

  if (contentJunk) return err('junk', detected, candidate);

  if (!grammar.hyphen && candidate.includes('-')) return err('hyphen', detected, candidate);

  const lower = candidate.toLowerCase();
  if (JUNK_TOKENS.has(lower)) return err('junk', detected, lower);
  if (grammar.reserved.has(lower)) return err('junk', detected, lower);
  if (lower.includes('@')) return err('junk', detected, lower);
  if (JUNK_SUFFIX.test(lower)) return err('junk', detected, lower);
  if (!grammar.charset.test(lower) || lower.length < grammar.min || lower.length > grammar.max) {
    return err(NON_ASCII.test(lower) ? 'unparseable' : 'junk', detected, lower);
  }

  return { platform, handle: lower, source };
}

/**
 * The `string|null` adapter over `parse()`, for callers that need no refusal reason.
 */
export function normalize (input, platform) {
  return parse(input, platform).handle ?? null;
}
